import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Crown from "../components/Crown";
import PasswordInput from "../components/PasswordInput";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Your estate awaits.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-textmain flex items-center justify-center px-6 py-12">
      <form
        onSubmit={submit}
        data-testid="register-form"
        className="w-full max-w-md regal-card p-10"
      >
        <div className="flex items-center gap-2 mb-6">
          <Crown className="w-8 h-8" strokeWidth={1.2} />
          <div>
            <span className="font-heading text-2xl block leading-none">Aurethion</span>
            <span className="wordmark-nexus text-[11px]">Crown</span>
          </div>
        </div>
        <p className="label-eyebrow">Create Account</p>
        <h2 className="font-heading text-3xl mt-2 mb-8">Stake your throne</h2>

        <label className="block label-eyebrow mb-2" htmlFor="name">Full Name</label>
        <input
          id="name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="register-name-input"
          className="w-full mb-5 bg-obsidian border border-seam rounded-md px-4 py-3 text-textmain placeholder-[#52525B] focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 transition-colors"
          placeholder="Your name"
        />

        <label className="block label-eyebrow mb-2" htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="register-email-input"
          className="w-full mb-5 bg-obsidian border border-seam rounded-md px-4 py-3 text-textmain placeholder-[#52525B] focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 transition-colors"
          placeholder="you@domain.com"
        />

        <label className="block label-eyebrow mb-2" htmlFor="password">Password</label>
        <div className="mb-8">
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testid="register-password-input"
            autoComplete="new-password"
            placeholder="At least 6 characters"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit-btn"
          className="w-full bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-6 py-3 transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />}
          Create my account
        </button>

        <p className="text-sm text-textmuted text-center mt-6">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-champagne hover:text-champagne-soft transition-colors"
            data-testid="link-login"
          >
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
};

export default Register;
